import { ouvrirLAide, useAideOuverte } from './aideOuverte';
import { useGesteDuTuto, useLancerPartie, useTuto } from './useTuto';
import { useEcranDuTuto } from './ecranDuTuto';
import { LegendeCarte } from '../carte';
import { useEchap } from '../partage/useEchap';
import { AIDE_DE_LA_CARTE, AIDE_EN_REECRITURE, EN_TETE_DE_L_AIDE } from '../partage/vocabulaire';
import './Aide.css';

/**
 * L'Aide (ticket 11 de retours-testeurs).
 *
 * Une ampoule ronde en bas à droite, sur tous les écrans ; survolée, elle dit « Aide ». Un
 * clic ouvre juste au-dessus la liste des parties du tuto, en quatre niveaux ; un second clic
 * la referme. Une partie cliquée se lance seule, avec le bandeau et l'anneau du tuto, sur un
 * petit voyage de démo neuf ; une partie faite prend une coche, que le serveur retient. Le
 * titre d'un niveau (sauf la présentation) lance toutes ses parties à la suite, sur un seul
 * voyage (ticket 17 de retours-testeurs).
 *
 * À la fin de la présentation, l'anneau entoure l'ampoule : la cliquer termine la
 * présentation et ouvre la liste, comme « Ouvrir l'Aide ».
 *
 * Elle passe sous une fiche ouverte et sous les fenêtres : ce qui se fait là n'est jamais
 * caché par elle.
 *
 * Sur une Carte, elle commence par la légende de la carte (chantier `tete-du-projet`, modification
 * 02, décisions 6 et 7 d'Igor) : ce que disent les dessins et les gestes, et la touche h pour
 * Hiérarchiser, qui n'a plus de bouton. Le « ? » à côté des angles est parti. Ailleurs, rien ne
 * change.
 */
export function Aide() {
  const tuto = useTuto();
  const aide = tuto.data?.aide ?? null;
  const vue = tuto.data?.tuto ?? null;
  const ouverte = useAideOuverte();
  const lancer = useLancerPartie();
  const geste = useGesteDuTuto();
  const ecran = useEcranDuTuto();
  const surUneCarte = ecran?.lieu === 'feuille' && ecran.vue === 'carte';

  // À côté de tout, jamais couverte : Échap la ferme, quoi qu'il y ait par-dessus.
  useEchap(ouverte, () => ouvrirLAide(false), 'a-cote');

  function basculer() {
    // La dernière étape d'une partie montre l'ampoule : la cliquer, c'est « Ouvrir l'Aide ».
    // Dans un niveau lancé en entier, c'est « Retour à l'Aide » : le niveau s'arrête là.
    if (vue?.phase === 'bandeau' && vue.fin) {
      geste.mutate({ geste: vue.fin.suivante ? 'arreter' : 'passer', vue, rouvrir: true });
      return;
    }
    ouvrirLAide(!ouverte);
  }

  const refuse = lancer.isError || (lancer.isSuccess && lancer.data.tuto === null);

  return (
    <div className="aide">
      {ouverte && aide && (
        <section className="aide-panneau" aria-label="Aide">
          <button type="button" className="aide-fermer" onClick={() => ouvrirLAide(false)} aria-label="fermer l’Aide">
            ×
          </button>
          <header className="aide-tete">
            <h2>Aide</h2>
            <p>{EN_TETE_DE_L_AIDE}</p>
            <p className="aide-en-reecriture" role="note">
              {AIDE_EN_REECRITURE}
            </p>
          </header>

          <div className="aide-liste">
            {surUneCarte && (
              <div className="aide-carte">
                <h3 className="aide-niveau">{AIDE_DE_LA_CARTE}</h3>
                <LegendeCarte enAvant={ecran.grossit ?? null} />
              </div>
            )}
            {aide.niveaux.map((niveau) => (
              <div key={niveau.nom}>
                <h3 className="aide-niveau">
                  {/* Ticket 17 : le titre d'un niveau lance toutes ses parties à la suite. */}
                  {niveau.seLance ? (
                    <button
                      type="button"
                      className={`aide-niveau-lancer${aide.niveauEnCours === niveau.numero ? ' est-en-cours' : ''}`}
                      onClick={() => lancer.mutate({ niveau: niveau.numero })}
                      disabled={lancer.isPending}
                      title="Toutes ses parties, à la suite"
                    >
                      <span>{niveau.nom}</span>
                      <span className="aide-lancer">
                        {aide.niveauEnCours === niveau.numero ? 'En cours' : 'Tout lancer'}
                      </span>
                    </button>
                  ) : (
                    niveau.nom
                  )}
                </h3>
                {/* Ticket 20 : ce que le niveau apporte, en une ligne. */}
                {niveau.ligne && <p className="aide-niveau-ligne">{niveau.ligne}</p>}
                {niveau.parties.map((partie) => {
                  const enCours = aide.enCours === partie.cle;
                  return (
                    <button
                      key={partie.cle}
                      type="button"
                      className={`aide-partie${partie.faite ? ' est-faite' : ''}${enCours ? ' est-en-cours' : ''}`}
                      onClick={() => lancer.mutate(partie.cle)}
                      disabled={lancer.isPending}
                    >
                      <span className="aide-numero" aria-hidden="true">
                        {partie.faite ? '✓' : partie.numero}
                      </span>
                      <span className="aide-mots">
                        <span className="aide-titre">{partie.titre}</span>
                        {partie.description && <span className="aide-description">{partie.description}</span>}
                      </span>
                      <span className="aide-lancer">{enCours ? 'En cours' : 'Lancer'}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <footer className="aide-pied">
            {refuse ? (
              <em className="aide-refus">Ça n’a pas marché, réessaie</em>
            ) : (
              `${aide.faites} geste${aide.faites > 1 ? 's' : ''} sur ${aide.total} déjà faits`
            )}
          </footer>
        </section>
      )}

      <button
        type="button"
        className={`aide-ampoule${ouverte ? ' est-ouverte' : ''}`}
        data-tuto="ampoule"
        onClick={basculer}
        aria-expanded={ouverte}
        aria-label="Aide"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 18h6" />
          <path d="M10 21h4" />
          <path d="M12 3a6 6 0 0 0-3.6 10.8c.7.6 1.1 1.3 1.1 2.2h5c0-.9.4-1.6 1.1-2.2A6 6 0 0 0 12 3z" />
        </svg>
        <span className="aide-info" aria-hidden="true">
          Aide
        </span>
      </button>
    </div>
  );
}
